import styles from "../ourCars.module.css";
import { useAppDispatch, useAppSelector } from "../../../../../../redux/hooks";
import { ActionCreatorWithOptionalPayload } from "@reduxjs/toolkit";
import { ACTIVE_PRICE_PATH, MPH_PATH, POWER_PATH, RETAIL_PRICE_GBP_PATH, TOP_SPEED_PATH } from "../ourCars-constants";
import {
  publishBHP,
  publishFilterHeaderHeight,
  publishRRP,
  publishTicketPrice,
  publishTopMPH,
  publishZeroTo60,
} from "../../../../../../redux/features/cars/carsSlice";
import { FilterHeaderProps } from "../types";
import { useEffect, useRef } from "react";

const badgeClasses = `${styles.rangeFilterBadge} flex p-1 pl-2 text-xs bg-[#FF8200] text-white font-semibold  border border-orange-500 rounded-full`;

const publishAction: {
  [key: string]: ActionCreatorWithOptionalPayload<
    {
      value: number[];
    },
    string
  >;
} = {
  [ACTIVE_PRICE_PATH]: publishTicketPrice,
  [RETAIL_PRICE_GBP_PATH]: publishRRP,
  [MPH_PATH]: publishTopMPH,
  [TOP_SPEED_PATH]: publishZeroTo60,
  [POWER_PATH]: publishBHP,
};

const FilterHeader = ({ onClearAll, onRangeFilterChange }: FilterHeaderProps) => {
  const ticketPriceRange = useAppSelector(state => state.cars.ticketPrice);
  const rrpRange = useAppSelector(state => state.cars.rrp);
  const topMPHRange = useAppSelector(state => state.cars.topMPH);
  const zeroTo60Range = useAppSelector(state => state.cars.zeroTo60);
  const bhpRange = useAppSelector(state => state.cars.bhp);
  const selectedFilter = useAppSelector(state => state.cars.filter);
  const dispatch = useAppDispatch();
  const ref = useRef<HTMLDivElement>();

  useEffect(() => {
    if (ref.current) {
      dispatch(publishFilterHeaderHeight({ value: ref.current.clientHeight }));
    }
  }, [ticketPriceRange, rrpRange, topMPHRange, zeroTo60Range, bhpRange, selectedFilter]);

  const resetRangeFilter = (path: string) => {
    onRangeFilterChange(path, []);
    dispatch(publishAction[path]({ value: [] }));
  };

  return (
    <div ref={ref}>
      <div className="flex py-4">
        <h1 className="text-3xl pl-4 filtersHeader">Filters</h1>
        <button aria-label="Clear All Filters" className={`${styles.clearAllFilters} ml-auto`} onClick={onClearAll}>
          CLEAR ALL
        </button>
      </div>
      <div className="grid gap-2 grid-cols-2">
        {selectedFilter && (
          <span className={badgeClasses} onClick={onClearAll}>
            {selectedFilter.value}
          </span>
        )}
        {ticketPriceRange.length > 0 && (
          <span className={badgeClasses} onClick={() => resetRangeFilter(ACTIVE_PRICE_PATH)}>
            Ticket Price:
            {`£${Math.floor(ticketPriceRange[0])} to £${Math.ceil(ticketPriceRange[1])}`}
          </span>
        )}
        {rrpRange.length > 0 && (
          <span className={badgeClasses} onClick={() => resetRangeFilter(RETAIL_PRICE_GBP_PATH)}>
            RRP: {`£${Math.floor(rrpRange[0])} to £${Math.ceil(rrpRange[1])}`}
          </span>
        )}
        {topMPHRange.length > 0 && (
          <span className={badgeClasses} onClick={() => resetRangeFilter(MPH_PATH)}>
            Top MPH:
            {`£${Math.floor(topMPHRange[0])} to £${Math.ceil(topMPHRange[1])}`}
          </span>
        )}
        {zeroTo60Range.length > 0 && (
          <span className={badgeClasses} onClick={() => resetRangeFilter(TOP_SPEED_PATH)}>
            0-60 (s):
            {`£${Math.floor(zeroTo60Range[0])} to £${Math.ceil(zeroTo60Range[1])}`}
          </span>
        )}
        {bhpRange.length > 0 && (
          <span className={badgeClasses} onClick={() => resetRangeFilter(POWER_PATH)}>
            BHP: {`£${Math.floor(bhpRange[0])} to £${Math.ceil(bhpRange[1])}`}
          </span>
        )}
      </div>
    </div>
  );
};

export default FilterHeader;
